"use client";

import React from "react";
import { Phone, Shield, Clock, Star, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function CompanionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-lg bg-secondary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-secondary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">Estimate Requested</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-secondary/50 hover:bg-secondary/10 rounded-lg">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="bg-transparent border-0 border-b-2 border-primary/20 focus-visible:ring-0 focus-visible:border-secondary transition-colors" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Enter your full legal name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-transparent border-0 border-b-2 border-primary/20 focus-visible:ring-0 focus-visible:border-secondary transition-colors" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">We'll send your custom quote here.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(330) 555-0199" className="bg-transparent border-0 border-b-2 border-primary/20 focus-visible:ring-0 focus-visible:border-secondary transition-colors" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Best number for a quick consultation.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="occasion" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Primary Service</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-transparent border-0 border-b-2 border-primary/20 focus:ring-0 focus:border-secondary w-full">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {["Elite Mowing", "Architectural Landscaping", "Premium Mulching", "Custom Patios", "Seasonal Cleanup"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormDescription className="text-xs opacity-70">Choose the main focus of your project.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="event_date" rules={{
        required: "Preferred Date is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Preferred Start Date</FormLabel>
              <FormControl>
                <Input type="date" className="bg-transparent border-0 border-b-2 border-primary/20 focus-visible:ring-0 focus-visible:border-secondary block w-full" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">When would you like us to begin?</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="duration_hours" rules={{
        required: "Property size is required",
        min: {
          value: 1,
          message: "Minimum 1 acre"
        },
        max: {
          value: 100,
          message: "Maximum 100 acres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Property Size (Acres)</FormLabel>
              <FormControl>
                <Input type="number" min="1" max="100" placeholder="e.g., 2" className="bg-transparent border-0 border-b-2 border-primary/20 focus-visible:ring-0 focus-visible:border-secondary transition-colors" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Approximate size of the work area.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80 font-default">Project Vision</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your dream outdoor space or specific requirements..." className="resize-none bg-transparent border-0 border-b-2 border-primary/20 focus-visible:ring-0 focus-visible:border-secondary min-h-[120px] transition-colors" {...field} />
            </FormControl>
            <FormDescription className="text-xs opacity-70">Optional: share any specific design inspirations or constraints.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive-foreground">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-default font-bold bg-secondary text-white hover:bg-secondary/90 transition-all shadow-md rounded-lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Request...
          </> : 'Send Message'}
      </Button>
      
      <p className="text-center text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1.5">
        <Shield className="size-3.5 text-chart-3" />
        Your privacy is our priority. All data is securely handled.
      </p>
    </Form>;
}
export default function SecureYourCompanion() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="241608" id="start-your-transformation" className="dark bg-background text-foreground py-24 lg:py-32 relative overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-lg bg-primary/10 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-lg bg-secondary/5 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <div className={`lg:col-span-5 space-y-8 transition-all duration-1000 ease-out ${isSectionVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-secondary/10 border border-secondary/20 text-secondary text-sm font-bold tracking-widest uppercase">
                <Star className="size-4 fill-chart-3 text-chart-3" />
                <span>Elite Landscaping</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-default font-black uppercase leading-tight text-foreground">
                Start Your <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-secondary/70">
                  Transformation
                </span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed font-default">
                We work hard so you can enjoy more. Connect with our project managers to bring your architectural outdoor vision to life.
              </p>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-border/50 group">
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent z-10" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/landscaping_consultation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/landscaping_consultation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/landscaping_consultation.png-wvc-sizes" alt="Elite Landscaping Consultation" className="w-full h-[300px] object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              
              <div className="absolute bottom-0 left-0 w-full p-6 z-20">
                <div className="backdrop-blur-md bg-background/60 border border-white/10 rounded-xl p-5 flex items-center gap-4 shadow-lg">
                  <div className="size-12 rounded-lg bg-chart-3/20 flex items-center justify-center shrink-0">
                    <Phone className="size-5 text-chart-3" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground font-bold uppercase tracking-wider mb-1">Direct Project Line</p>
                    <Link to="tel:+13305169401" className="text-xl font-default font-bold text-foreground hover:text-secondary transition-colors">
                      330.516.9401
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="flex items-center gap-3">
                <Clock className="size-5 text-secondary" />
                <span className="text-sm text-muted-foreground font-default">Reliable Scheduling</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="size-5 text-secondary" />
                <span className="text-sm text-muted-foreground font-default">Elite Craftsmanship</span>
              </div>
            </div>
          </div>

          {}
          <div ref={formRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${isFormVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="bg-card/40 backdrop-blur-xl border border-border/50 rounded-3xl p-6 sm:p-10 shadow-xl relative">
              {}
              <div className="absolute top-0 left-0 w-16 h-16 border-t border-l border-secondary/30 rounded-tl-3xl pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-16 h-16 border-b border-r border-secondary/30 rounded-br-3xl pointer-events-none" />
              
              <div className="mb-8">
                <h3 className="text-2xl font-default font-bold uppercase text-foreground mb-2">Request an Estimate</h3>
                <p className="text-sm text-muted-foreground font-default">Provide your project details and our owners, Tyler or Sean, will personally review your request.</p>
              </div>

              <FormProvider formKey="transformation_form_home" formId="transformation_form_home" sectionName="Start Your Transformation" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              occasion: "",
              event_date: "",
              duration_hours: "",
              additional_details: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              occasion: {
                required: true
              },
              event_date: {
                required: true
              },
              duration_hours: {
                required: true,
                min: 1,
                max: 100
              }
            }} submitText="Send Message" successMessage="Your request has been received. Our team will contact you shortly to schedule a site visit." errorMessage="There was an error submitting your request. Please call us directly at 330.516.9401.">
                <CompanionForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}